import React, { useEffect, useRef, useState } from "react";
import "./NumberInput.css";
import debounce from "lodash.debounce";
import throttle from "lodash.throttle";

const NumberInput = ({
  label,
  postLabel,
  onChange,
  value,
  min,
  max,
  onFocusOut,
  disabled,
  step = 1,
  customBtnMargin,
}) => {
  // localValue drives the UI so the number updates instantly on every click.
  // valueRef is the SYNCHRONOUS math base, so rapid clicks build on the latest
  // value instead of a stale render closure (10 -> 11 -> 12 -> 13 never skips).
  // interactingRef ignores the parent's async / out-of-order echo for a short
  // window after each change, so a laggy Redux round-trip can't yank the number
  // back down to an earlier value.
  const [localValue, setLocalValue] = useState(value);
  const valueRef = useRef(value);
  const interactingRef = useRef(false);
  const idleTimerRef = useRef(null);

  // Adopt the external value ONLY when the user isn't mid-interaction.
  useEffect(() => {
    if (!interactingRef.current) {
      valueRef.current = value;
      setLocalValue(value);
    }
  }, [value]);

  // Debounced focus out
  const debouncedFocusOutRef = useRef(
    debounce(() => {
      if (onFocusOut) onFocusOut();
    }, 300)
  );

  const markInteracting = () => {
    interactingRef.current = true;
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      interactingRef.current = false;
    }, 400);
  };

  // Clamp + commit a new value: sync ref (for the next rapid click) + instant
  // local display, then notify the parent.
  const updateValue = (newValue) => {
    if (disabled || !onChange) return;
    const clampedValue = Math.max(
      isNaN(min) ? -Infinity : min,
      Math.min(isNaN(max) ? Infinity : max, newValue)
    );
    if (clampedValue === Number(valueRef.current)) return;
    valueRef.current = clampedValue;
    setLocalValue(clampedValue);
    markInteracting();
    onChange(clampedValue);
    debouncedFocusOutRef.current();
  };

  const handleInc = () => {
    if (disabled || !onChange) return;
    updateValue(Number(valueRef.current) + step);
  };

  const handleDec = () => {
    if (disabled || !onChange) return;
    updateValue(Number(valueRef.current) - step);
  };

  // Throttled scroll to prevent multiple triggers per tick
  const throttledScroll = useRef(
    throttle((direction) => {
      updateValue(Number(valueRef.current) + direction * step);
    }, 200)
  );

  useEffect(() => {
    return () => {
      debouncedFocusOutRef.current.cancel();
      throttledScroll.current.cancel();
      clearTimeout(idleTimerRef.current);
    };
  }, []);

  const handleMouseWheel = (event) => {
    if (disabled || !onChange) return;
    event.preventDefault();
  };

  const handleKeyDown = (event) => {
    if (disabled || !onChange) return;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      updateValue(Number(valueRef.current) + step);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      updateValue(Number(valueRef.current) - step);
    }
  };

  const handleChange = (e) => {
    if (disabled || !onChange) return;
    const inputVal = e.target.value;
    // Allow free typing (including a temporarily empty field) — don't clamp
    // mid-type, so a min of 15 doesn't block typing "13".
    setLocalValue(inputVal);
    markInteracting();
    if (inputVal !== "" && !isNaN(inputVal)) {
      valueRef.current = Number(inputVal);
      onChange(Number(inputVal));
    }
  };

  return (
    <div className="numContainer">
      {label && <span className="number-label">{label}</span>}

      <div
        className={"numContainer-component " + (disabled ? "disabled" : "")}
        style={{
          marginLeft:
            label === "Gutter"
              ? "12px"
              : label === "Ragged Gutter"
              ? "15px"
              : "6px",
          ...customBtnMargin,
        }}
      >
        <span className="inputContainer">
          <input
            type="number"
            className="form-control"
            value={localValue}
            step={step}
            min={min}
            max={max}
            onChange={handleChange}
            onWheel={handleMouseWheel}
            onKeyDown={handleKeyDown}
            onBlur={onFocusOut}
            disabled={disabled}
          />
          {/* {postLabel} */}
          <span className={`postLValue ${postLabel ? "hasValue" : ""}`}>
          {postLabel}
        </span>
        </span>
        <div className="btnContainer">
          <div onClick={handleInc} className="top-btn">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon-tabler icon-tabler-chevron-up"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="#c7c0d9"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <polyline points="6 15 12 9 18 15"></polyline>
            </svg>
          </div>
          <div onClick={handleDec} className="down-btn">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon-tabler icon-tabler-chevron-down"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="#c7c0d9"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default NumberInput;